import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../contexts/I18nProvider';
import { Check } from 'lucide-react';

const CheckIcon = ({ className = "w-5 h-5" }) => (
  <Check className={`${className} text-green-600`} />
);

export const PricingSection = () => {
  const { t, getResource } = useTranslation();
  const navigate = useNavigate();

  const plans = [
    {
      id: 'free',
      name: t('landing.pricing.free.name'),
      price: t('landing.pricing.free.price'),
      description: t('landing.pricing.free.description'),
      features: getResource('landing.pricing.free.features') || [],
      highlighted: false,
      cta: t('landing.pricing.free.cta'),
    },
    {
      id: 'monthly',
      name: t('landing.pricing.monthly.name'),
      price: t('landing.pricing.monthly.price'),
      priceSubtext: t('landing.pricing.monthly.priceSubtext'),
      description: t('landing.pricing.monthly.description'),
      features: getResource('landing.pricing.monthly.features') || [],
      highlighted: true,
      badge: t('landing.pricing.badge.mostPopular'),
      cta: t('landing.pricing.monthly.cta'),
    },
    {
      id: 'annual',
      name: t('landing.pricing.annual.name'),
      price: t('landing.pricing.annual.price'),
      priceSubtext: t('landing.pricing.annual.priceSubtext'),
      description: t('landing.pricing.annual.description'),
      features: getResource('landing.pricing.annual.features') || [],
      highlighted: false,
      badge: t('landing.pricing.badge.save'),
      cta: t('landing.pricing.annual.cta'),
    },
  ];

  return (
    <section id="pricing" className="py-12 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
            {t('landing.pricing.title')}
          </h2>
          <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
            {t('landing.pricing.subtitle')}
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="mx-auto mt-16 max-w-7xl grid grid-cols-1 gap-8 overflow-visible lg:grid-cols-3">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative flex flex-col rounded-2xl border transition-all overflow-visible ${
                plan.highlighted
                  ? 'border-indigo-600 bg-gradient-to-br from-indigo-50/50 to-indigo-100/50 dark:from-indigo-900/30 dark:to-indigo-800/30 shadow-lg lg:ring-2 lg:ring-indigo-600'
                  : 'border-gray-200 bg-white/50 dark:bg-gray-900/50 dark:border-gray-700'
              }`}
            >
              <div className="p-8 sm:p-10">
                {/* Badge */}
                {plan.badge && (
                  <div className="mb-4">
                    <span
                      className={`inline-flex rounded-full px-4 py-1 text-sm font-semibold text-white ${
                        plan.highlighted ? 'bg-indigo-600' : 'bg-green-600'
                      }`}
                    >
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Plan Name */}
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {plan.name}
                </h3>

                {/* Price */}
                <div className="mt-6 flex items-baseline gap-x-2">
                  <span className="text-5xl font-bold text-gray-900 dark:text-white whitespace-nowrap">
                    {plan.price}
                  </span>
                  {plan.priceSubtext && (
                    <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                      {plan.priceSubtext}
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {plan.description}
                </p>

                {/* CTA Button */}
                <button
                  onClick={() => navigate('/pricing')}
                  className={`mt-8 w-full rounded-lg px-4 py-3 text-sm font-semibold transition-colors ${
                    plan.highlighted
                      ? 'bg-indigo-600 text-white hover:bg-indigo-500'
                      : 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {plan.cta}
                </button>

                {/* Divider */}
                <div className="mt-8 border-t border-gray-200 dark:border-gray-700"></div>

                {/* Features List */}
                <ul className="mt-8 space-y-4">
                  {Array.isArray(plan.features) ? (
                    plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-x-3">
                        <CheckIcon />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {feature}
                        </span>
                      </li>
                    ))
                  ) : null}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="mx-auto mt-16 max-w-2xl text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Tous les plans incluent accès complet à la base de données des langues achetées.
            Annulation possible à tout moment.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
